import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Download, Loader2, MailCheck, Pencil, UserRound } from "lucide-react";
import { toast } from "sonner";
import XeroContactLink from "./XeroContactLink";

const TYPE = { normal: "Standard (pay as you go)", on_account: "14-day account", florist: "Florist", partner: "Partner", paint_shop: "Paint shop" };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

export async function downloadText(path, fallbackName) {
  const token = localStorage.getItem("kc_token");
  const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api${path}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!r.ok) throw new Error("download failed");
  const blob = await r.blob();
  const name = (r.headers.get("Content-Disposition") || "").match(/filename=([^;]+)/)?.[1] || fallbackName;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = name; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}

function Row({ k, v, testid }) {
  return <div className="flex justify-between gap-3 border-b border-dashed py-1.5 text-sm last:border-0"><span className="text-muted-foreground">{k}</span><span className="text-right font-medium" data-testid={testid}>{v || "—"}</span></div>;
}

export default function CustomerDetailsDialog({ customer, onSaved }) {
  const [open, setOpen] = useState(false);
  const [d, setD] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (!open) return;
    setD(null);
    api.get(`/admin/users/${customer.id}/details`).then((r) => { setD(r.data); setForm({ name: r.data.name, email: r.data.email, phone: r.data.phone || "" }); setNote(r.data.admin_note || ""); })
      .catch((e) => { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setOpen(false); });
  }, [open, customer.id]);

  const save = async () => {
    setBusy("save");
    try {
      await api.patch(`/admin/users/${customer.id}/profile`, form);
      if (note !== (d.admin_note || "")) await api.patch(`/admin/users/${customer.id}/admin-note`, { admin_note: note });
      toast.success(form.email.trim().toLowerCase() !== d.email ? "Saved — email updated and marked verified" : "Account details saved");
      setOpen(false); onSaved?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't save"); } finally { setBusy(""); }
  };
  const resend = async () => {
    setBusy("resend");
    try { const { data } = await api.post(`/admin/users/${customer.id}/resend-verification`); toast.success(`Verification email sent to ${data.sent_to}`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(""); }
  };
  const download = async () => {
    setBusy("dl");
    try { await downloadText(`/admin/users/${customer.id}/details.txt`, "customer.txt"); } catch { toast.error("Download failed"); } finally { setBusy(""); }
  };
  const refreshXero = (data) => {
    if (data?.xero_eligible !== undefined) { setD((prev) => ({ ...prev, ...data })); return; }
    api.get(`/admin/users/${customer.id}/details`).then((r) => setD((prev) => ({ ...prev, ...r.data }))).catch(() => {});
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`edit-customer-${customer.id}`} className="h-8 gap-1 border-kilted-green text-kilted-green hover:bg-kilted-green/10">
          <UserRound className="h-3.5 w-3.5" /> Details
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl" data-testid="customer-details-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><UserRound className="h-5 w-5 text-kilted-gold" /> Account details — {customer.name}</DialogTitle>
          <DialogDescription>Everything captured at sign-up and since. Passwords are never stored in plain text and can't be viewed.</DialogDescription>
        </DialogHeader>
        {!d ? <Loader2 className="mx-auto h-6 w-6 animate-spin text-primary" /> : (
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-3">
              <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><Pencil className="h-3 w-3" /> Editable</p>
              <div><Label className="text-xs">Full name</Label><Input value={form.name} onChange={set("name")} data-testid="edit-customer-name" className="mt-1 h-9" /></div>
              <div><Label className="text-xs">Email {d.email_verified ? <span className="text-emerald-700">· verified</span> : <span className="text-amber-700">· not verified</span>}</Label><Input type="email" value={form.email} onChange={set("email")} data-testid="edit-customer-email" className="mt-1 h-9" /></div>
              <div><Label className="text-xs">Mobile</Label><Input value={form.phone} onChange={set("phone")} placeholder="04xx xxx xxx" data-testid="edit-customer-phone" className="mt-1 h-9" /></div>
              <div><Label className="text-xs">Internal admin note (never shown to the customer)</Label><Textarea value={note} onChange={(e) => setNote(e.target.value.slice(0, 1000))} rows={3} placeholder="e.g. Prefers afternoon calls · gate code 4421" data-testid="edit-customer-note" className="mt-1" /></div>
              {!d.email_verified && <Button size="sm" variant="outline" onClick={resend} disabled={!!busy} data-testid="resend-verification" className="gap-1.5"><MailCheck className="h-3.5 w-3.5" /> Resend verification email</Button>}
            </div>
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">On record</p>
              <Row k="Account type" v={TYPE[d.customer_type] || d.customer_type} testid="details-type" />
              <Row k="Role" v={d.role} />
              <Row k="Status" v={d.status} />
              {d.cancels_30d > 0 && <Row k="Cancels (30d)" v={<span className={d.frequent_canceller ? "font-semibold text-red-700" : ""} data-testid="details-cancels-30d">{d.cancels_30d}{d.frequent_canceller ? " — follow up before next booking" : ""}</span>} />}
              <Row k="Joined" v={fmt(d.created_at)} testid="details-joined" />
              <Row k="Last login" v={fmt(d.last_login)} />
              <Row k="Orders placed" v={String(d.order_count)} testid="details-orders" />
              <Row k="Referral code" v={d.referral_code} />
              <Row k="Referred by" v={d.referred_by} />
              <Row k="Google sign-in" v={d.google_linked ? "linked" : "no"} />
              {(d.business_name || d.abn) && <><Row k="Business" v={d.business_name} /><Row k="ABN" v={d.abn} /></>}
              <XeroContactLink userId={customer.id} details={d} onChanged={refreshXero} />
              <p className="mb-1 mt-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Saved addresses ({d.saved_addresses.length})</p>
              {d.saved_addresses.length === 0 ? <p className="text-xs text-muted-foreground">None saved yet.</p> : (
                <ul className="space-y-1 text-xs" data-testid="details-addresses">
                  {d.saved_addresses.map((a, i) => <li key={a.id || i} className="rounded border bg-secondary/40 px-2 py-1"><b>{a.label || "Address"}</b> · {a.line1}, {a.city} {a.postcode}{a.contact_name ? ` · ${a.contact_name}` : ""}</li>)}
                </ul>
              )}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={download} disabled={!d || !!busy} data-testid="download-customer-txt" className="gap-1.5"><Download className="h-4 w-4" /> Download .txt</Button>
          <Button onClick={save} disabled={!d || !!busy} data-testid="edit-customer-save" className="gap-2 bg-kilted-green hover:bg-kilted-greenLight">{busy === "save" && <Loader2 className="h-4 w-4 animate-spin" />} Save changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
